import type { MonthCell, RecurrenceOverride, RecurrenceRule, Task } from "@app/core";
import { addDays, endOfDay, endOfWeek, expandRecurringTask, fromIso, sameMatrixPos, startOfDay, startOfWeek } from "@app/core";

export interface DayCol {
  date: Date;
  tasks: Task[];
  /** Pre-filtered timed tasks for this day (stable ref so memoized columns can
   *  skip re-rendering when the day is reused across a window recenter). */
  timed: Task[];
}

/** Per-rule memo of a series' expanded virtuals, keyed by recurrence-rule id.
 *  Reused across renders so a single-task edit (which leaves every untouched
 *  series' source-task / rule / override-array reference intact) reuses their
 *  occurrences instead of re-running rrule for all of them. `from`/`to` are the
 *  PADDED block bounds the entry was expanded over (see `BLOCK_MS`), not the
 *  requested window. Hold one of these in a `useRef` and pass it to
 *  `expandDayCols` / `expandTasksWindowed`. */
export type RuleExpansionCache = Map<
  string,
  {
    task: Task;
    rule: RecurrenceRule;
    overrides: ReadonlyArray<RecurrenceOverride>;
    from: string;
    to: string;
    entries: Array<{ iso: string; task: Task }>;
  }
>;

/** Quantization grid for cached rule expansions: each series is expanded over
 *  epoch-aligned 64-day blocks COVERING the requested window, and the window is
 *  sliced out of the cached block by occurrence ref. Day/week stepping (which
 *  recenters the wide window every other step) and month flips then reuse every
 *  untouched series' cached block — with identity-stable virtuals, so day-ref
 *  compares hit the reference fast-path — instead of re-expanding all of them;
 *  only crossing a 64-day grid line re-expands. */
const BLOCK_MS = 64 * 86_400_000;
const DAY_MS = 86_400_000;

const EMPTY: ReadonlyArray<RecurrenceOverride> = [];

function localDayOrdinal(date: Date): number {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / DAY_MS;
}

/** Number of local calendar dates touched by an elapsed timed interval. The
 *  final millisecond belongs to the task, so an exact-midnight end does not
 *  create a continuation. This avoids assuming DST days are always 24 hours. */
function timedSpanDays(start: Date, durationMinutes: number): number {
  const durationMs = Math.max(0, durationMinutes) * 60_000;
  if (durationMs === 0) return 1;
  const lastTouched = new Date(start.getTime() + durationMs - 1);
  return Math.max(1, localDayOrdinal(lastTouched) - localDayOrdinal(start) + 1);
}

/** Window-aware replacement for `expandTasksForCalendar`: (a) window-filters
 *  non-recurring tasks in the single expansion pass — so per-day bucketing scans
 *  only the tiles in view, not the whole dataset — and (b) memoizes each series'
 *  virtuals per rule via the optional `cache`. Shared by the week/day track
 *  (`expandDayCols`) and the month grid (`useMonthCells`). */
export function expandTasksWindowed(
  tasks: Task[],
  rules: Record<string, RecurrenceRule>,
  overrides: Record<string, ReadonlyArray<RecurrenceOverride>>,
  from: Date,
  to: Date,
  cache?: RuleExpansionCache,
  /** Include multi-day TIMED tasks whose start is before `from` but whose
   *  duration spills into the window. ONLY the week/day track wants this (it
   *  renders continuation segments); agenda/reminders/month place tasks by
   *  their start day and must keep the strict filter (agenda would otherwise
   *  double-count them against its own `ancient` back-scan). */
  timedSpans = false,
): Task[] {
  const toKey = to.toISOString();
  // Padded, epoch-aligned expansion end (cache mode only). The start is
  // series-specific in timed-span mode because an occurrence can begin before
  // `from` and still occupy the requested window.
  const padTo = new Date(Math.ceil((to.getTime() + 1) / BLOCK_MS) * BLOCK_MS - 1);

  const expanded: Task[] = [];
  const seenRules = new Set<string>();
  for (const t of tasks) {
    if (t.deleted_at) continue;
    if (!t.recurrence_id) {
      if (intersectsWindow(t, from, to, timedSpans)) expanded.push(t);
      continue;
    }
    if (t.status !== "open") continue;
    const rule = rules[t.recurrence_id];
    if (!rule) continue;
    const ov = overrides[rule.id] ?? EMPTY;
    seenRules.add(rule.id);

    // Recurrence expansion normally selects by occurrence START. In timed-span
    // mode, look back by the series duration so an overnight occurrence whose
    // start is outside the window can still contribute its continuation. The
    // downstream interval/day bucketing clips away any non-intersecting edge.
    const expansionFrom = timedSpans && !t.all_day
      ? new Date(from.getTime() - Math.max(0, t.duration_minutes) * 60_000)
      : from;
    const expansionFromKey = expansionFrom.toISOString();

    if (!cache) {
      for (const v of expandRecurringTask(t, rule, ov, expansionFrom, to)) {
        if (!timedSpans || intersectsWindow(v, from, to, true)) expanded.push(v);
      }
      appendMovedSinglesInWindow(expanded, t, rule, ov, expansionFrom, from, to, timedSpans);
      continue;
    }
    const padFrom = new Date(Math.floor(expansionFrom.getTime() / BLOCK_MS) * BLOCK_MS);
    const hit = cache.get(rule.id);
    let entries: Array<{ iso: string; task: Task }>;
    // Valid while refs are unchanged AND the cached block covers the window.
    if (hit && hit.task === t && hit.rule === rule && hit.overrides === ov && hit.from <= expansionFromKey && hit.to >= toKey) {
      entries = hit.entries;
    } else {
      entries = expandRecurringTask(t, rule, ov, padFrom, padTo)
        .map((task) => ({ iso: occurrenceIsoOfVirtual(task), task }));
      cache.set(rule.id, { task: t, rule, overrides: ov, from: padFrom.toISOString(), to: padTo.toISOString(), entries });
    }
    // Slice the requested start/lookback window out of the cached block. A virtual's id ends
    // with its occurrence ref (`<sourceId>::v::<iso>`) — exactly the expansion
    // window criterion, and per-occurrence construction is window-independent,
    // so the slice equals a direct expansion over the effective bounds.
    for (let i = lowerBound(entries, expansionFromKey); i < entries.length && entries[i]!.iso <= toKey; i++) {
      const occurrence = entries[i]!.task;
      if (!timedSpans || intersectsWindow(occurrence, from, to, true)) expanded.push(occurrence);
    }
    appendMovedSinglesInWindow(expanded, t, rule, ov, expansionFrom, from, to, timedSpans);
  }
  if (cache) for (const id of cache.keys()) if (!seenRules.has(id)) cache.delete(id); // drop departed series

  return expanded;
}

/** A single override keeps its immutable recurrence reference even when its
 * effective tile moves or grows. If that reference is outside the expansion
 * range but the overridden tile intersects the requested window, expanding
 * only by the reference makes it disappear (and exact notification/calendar
 * jumps can land on an empty day). Validate and synthesize just those
 * exceptional refs; normal in-range occurrences still use the cached bulk
 * expansion above. */
function appendMovedSinglesInWindow(
  out: Task[],
  source: Task,
  rule: RecurrenceRule,
  overrides: ReadonlyArray<RecurrenceOverride>,
  expansionFrom: Date,
  from: Date,
  to: Date,
  timedSpans: boolean,
): void {
  const fromRef = expansionFrom.toISOString();
  const toRef = to.toISOString();
  const added = new Set<string>();
  let bounds: OverrideBounds | null = null;

  for (const override of overrides) {
    if (override.range !== "single" || override.status === "cancelled") continue;
    // Strict start-day callers only need moved occurrences. Timed-span callers
    // must also recover an occurrence whose start stayed put but whose single
    // override made it long enough to continue into this window.
    if (!override.due_at && (!timedSpans || override.duration_minutes === null)) continue;
    const ref = override.recurrence_id_ref;
    if (ref >= fromRef && ref <= toRef) continue;
    // `expandRecurringTask` below builds an rrule instance per override, and
    // EVERY out-of-window override reaches it — on a long-lived calendar that is
    // thousands of expansions to recover a handful of tiles, and it dominated
    // every windowed expansion in the app. The override already carries the
    // fields the window test needs, so reject the impossible ones first.
    bounds ??= overrideBounds(source, overrides);
    if (!overrideMayIntersect(override, bounds, from, to, timedSpans)) continue;
    const at = fromIso(ref);
    if (!Number.isFinite(at.getTime())) continue;
    const occurrence = expandRecurringTask(source, rule, overrides, at, at)[0];
    if (!occurrence || added.has(occurrence.id) || !intersectsWindow(occurrence, from, to, timedSpans)) continue;
    added.add(occurrence.id);
    out.push(occurrence);
  }
}

interface OverrideBounds {
  /** Longest duration any effective resolution of an occurrence can have. */
  maxDurationMinutes: number;
  /** Whether any resolution can be all-day (the wider of the two window tests). */
  maybeAllDay: boolean;
}

/** Widest shape the series' occurrences can take. A `thisandfuture` override can
 *  change a later occurrence's duration or all-day-ness, so the bound is taken
 *  over the source AND every override — being too permissive only costs an
 *  expansion that the exact test then rejects, whereas being too strict would
 *  drop a real tile. */
function overrideBounds(source: Task, overrides: ReadonlyArray<RecurrenceOverride>): OverrideBounds {
  let maxDurationMinutes = Math.max(0, source.duration_minutes);
  let maybeAllDay = source.all_day;
  for (const override of overrides) {
    if (override.duration_minutes !== null) maxDurationMinutes = Math.max(maxDurationMinutes, override.duration_minutes);
    if (override.all_day) maybeAllDay = true;
  }
  return { maxDurationMinutes, maybeAllDay };
}

/** Conservative twin of {@link intersectsWindow} evaluated straight off the
 *  override, without expanding the series. Only ever returns true too often. */
function overrideMayIntersect(
  override: RecurrenceOverride,
  bounds: OverrideBounds,
  from: Date,
  to: Date,
  timedSpans: boolean,
): boolean {
  const start = fromIso(override.due_at ?? override.recurrence_id_ref);
  if (!Number.isFinite(start.getTime())) return true; // let the exact path decide
  const startDay = startOfDay(start);
  if (startDay > to) return false;
  if (bounds.maybeAllDay) {
    const endDay = addDays(startDay, Math.max(0, Math.ceil(bounds.maxDurationMinutes / 1440) - 1));
    return endDay >= from;
  }
  if (timedSpans && startDay < from) {
    return start.getTime() + bounds.maxDurationMinutes * 60_000 > from.getTime();
  }
  return startDay >= from;
}

function occurrenceIsoOfVirtual(task: Task): string {
  const marker = "::v::";
  const index = task.id.indexOf(marker);
  return index >= 0 ? task.id.slice(index + marker.length) : task.due_at ?? "";
}

function lowerBound(entries: ReadonlyArray<{ iso: string }>, target: string): number {
  let lo = 0;
  let hi = entries.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (entries[mid]!.iso < target) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** Expand `count` consecutive day-columns starting at `start` (00:00). */
export function expandDayCols(
  tasks: Task[],
  rules: Record<string, RecurrenceRule>,
  overrides: Record<string, ReadonlyArray<RecurrenceOverride>>,
  start: Date,
  count: number,
  cache?: RuleExpansionCache,
): DayCol[] {
  const from = startOfDay(start);
  const to = endOfDay(addDays(from, count - 1));
  // timedSpans: the track renders continuation segments, so multi-day timed
  // tasks must survive their start day scrolling out of the window.
  const expanded = expandTasksWindowed(tasks, rules, overrides, from, to, cache, true);

  return bucketDayCols(expanded, from, count, true);
}

/**
 * Bucket already-expanded/window-filtered tasks into consecutive day columns in
 * one pass. This preserves `tasksOnDay` ordering (input order within each day)
 * without scanning the same expanded task list once per rendered day.
 */
export function bucketDayCols(tasks: Task[], start: Date, count: number, timedSpans = false): DayCol[] {
  const from = startOfDay(start);
  const buckets = Array.from({ length: count }, () => [] as Task[]);

  for (const task of tasks) {
    if (!task.due_at) continue;
    const startAt = fromIso(task.due_at);
    const firstDay = startOfDay(startAt);
    const firstIdx = Math.round((firstDay.getTime() - from.getTime()) / DAY_MS);
    // With timedSpans, a multi-day TIMED task occupies every day its duration
    // touches (so continuation days' DayCol refs invalidate on its edits, and a
    // pre-window start still lands in the window); `timed` below stays a
    // start-day list, so DayColumn never renders it as a start block twice.
    const span = task.all_day
      ? task.duration_minutes > 1440 ? Math.ceil(task.duration_minutes / 1440) : 1
      : timedSpans
        ? timedSpanDays(startAt, task.duration_minutes)
        : 1;
    const lastIdx = firstIdx + span - 1;
    for (let idx = Math.max(0, firstIdx); idx <= Math.min(count - 1, lastIdx); idx++) buckets[idx]!.push(task);
  }

  return buckets.map((dayTasks, i) => {
    const date = addDays(from, i);
    return {
      date,
      tasks: dayTasks,
      timed: dayTasks.filter((t) => !t.all_day && t.due_at && startOfDay(fromIso(t.due_at)).getTime() === date.getTime()),
    };
  });
}

/** Month-grid equivalent of `bucketDayCols`, also one-pass over expanded tasks. */
export function bucketMonthCells(anchor: Date, tasks: Task[], weekStartsOn: 0 | 1): MonthCell[] {
  const monthStart = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const monthEnd = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  const gridStart = startOfDay(startOfWeek(monthStart, { weekStartsOn }));
  const gridEnd = startOfDay(endOfWeek(monthEnd, { weekStartsOn }));
  const dates: Date[] = [];
  for (let cursor = gridStart; cursor <= gridEnd; cursor = addDays(cursor, 1)) dates.push(cursor);
  const cols = bucketDayCols(tasks, gridStart, dates.length);
  return cols.map((col) => ({
    date: col.date,
    inMonth: col.date.getMonth() === anchor.getMonth(),
    tasks: col.tasks,
  }));
}

/** True when two task lists render identically. Tasks are compared positionally
 *  with a reference fast-path (untouched tasks — including cached virtuals —
 *  keep their object identity); regenerated virtuals fall back to a value
 *  compare. Used to preserve day/cell refs across edits so memoized columns and
 *  month cells skip re-rendering. */
export function sameTasks(a: ReadonlyArray<Task>, b: ReadonlyArray<Task>): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i]!;
    const y = b[i]!;
    if (x !== y && !sameTaskRenderSignature(x, y)) return false;
  }
  return true;
}

export function sameTaskRenderSignature(a: Task, b: Task): boolean {
  return a.id === b.id &&
    a.title === b.title &&
    a.notes === b.notes &&
    a.due_at === b.due_at &&
    a.deadline_at === b.deadline_at &&
    a.all_day === b.all_day &&
    a.duration_minutes === b.duration_minutes &&
    a.priority === b.priority &&
    // matrix_pos drives the CONTINUOUS block color: two positions in the same
    // priority band render as distinct hues, so a position-only edit must
    // break the signature even though the derived flag doesn't change.
    sameMatrixPos(a.matrix_pos, b.matrix_pos) &&
    a.custom_color === b.custom_color &&
    a.is_event === b.is_event &&
    a.location === b.location &&
    sameStringArray(a.label_ids, b.label_ids) &&
    sameNumberArray(a.reminder_offsets, b.reminder_offsets) &&
    a.tz === b.tz &&
    sameStringArray(a.topic_ids, b.topic_ids) &&
    a.calendar_id === b.calendar_id &&
    a.status === b.status &&
    a.recurrence_id === b.recurrence_id &&
    a.parent_id === b.parent_id &&
    a.virtual === b.virtual &&
    a.subtask_parent_id === b.subtask_parent_id &&
    a.subtask_order === b.subtask_order &&
    a.deleted_at === b.deleted_at;
}

function sameStringArray(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

function sameNumberArray(a: ReadonlyArray<number>, b: ReadonlyArray<number>): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/** Whether a task's tile(s) could fall within [from, to]. Mirrors `tasksOnDay`'s
 *  placement: timed/single-day tasks sit on their start day; only multi-day
 *  all-day tasks span (and thus can start before `from` yet still intersect).
 *  With `timedSpans`, multi-day TIMED tasks (cross-day resize) also count as
 *  spanning, so their continuation segments survive the start day scrolling
 *  out of the window. */
function intersectsWindow(t: Task, from: Date, to: Date, timedSpans = false): boolean {
  if (!t.due_at) return false;
  const startDay = startOfDay(fromIso(t.due_at));
  if (startDay > to) return false;
  if (t.all_day && t.duration_minutes > 1440) {
    const endDay = addDays(startDay, Math.ceil(t.duration_minutes / 1440) - 1);
    return endDay >= from;
  }
  if (timedSpans && !t.all_day && startDay < from) {
    return fromIso(t.due_at).getTime() + Math.max(0, t.duration_minutes) * 60_000 > from.getTime();
  }
  return startDay >= from;
}
